// Compute a display box for a signature image so it scales consistently and
// aspect-correctly in both the PDF (pdfmake) and Word (docx) exports. We parse
// the intrinsic pixel size from the PNG/JPEG header (self-contained — no image
// decoding), then fit it inside maxW × maxH preserving the aspect ratio.

function intrinsicSize(dataUri: string): { w: number; h: number } | null {
  try {
    const m = /^data:image\/(png|jpe?g);base64,(.+)$/i.exec(dataUri);
    if (!m) return null;
    const isPng = m[1].toLowerCase() === "png";
    const bin = atob(m[2]);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    if (isPng) {
      if (bytes.length < 24) return null;
      const w = (bytes[16] << 24) | (bytes[17] << 16) | (bytes[18] << 8) | bytes[19];
      const h = (bytes[20] << 24) | (bytes[21] << 16) | (bytes[22] << 8) | bytes[23];
      return w > 0 && h > 0 ? { w, h } : null;
    }
    // JPEG — scan segments for a Start-Of-Frame marker carrying the dimensions.
    let i = 2;
    while (i + 9 < bytes.length) {
      if (bytes[i] !== 0xff) { i++; continue; }
      const marker = bytes[i + 1];
      if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
        const h = (bytes[i + 5] << 8) | bytes[i + 6];
        const w = (bytes[i + 7] << 8) | bytes[i + 8];
        return w > 0 && h > 0 ? { w, h } : null;
      }
      const len = (bytes[i + 2] << 8) | bytes[i + 3];
      if (len <= 0) break;
      i += 2 + len;
    }
    return null;
  } catch {
    return null;
  }
}

/** A box (in the caller's units) scaled to fit within maxW × maxH, preserving
 * the image's aspect ratio. Falls back to a 3:1 signature shape if unreadable. */
export function signatureBox(dataUri: string, maxW: number, maxH: number): { width: number; height: number } {
  const dims = intrinsicSize(dataUri) || { w: 3, h: 1 };
  const aspect = dims.w / dims.h;
  let width = maxW;
  let height = maxW / aspect;
  if (height > maxH) { height = maxH; width = maxH * aspect; }
  return { width: Math.round(width), height: Math.round(height) };
}
